// Cookie Management Utilities
export interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  performance: boolean;
  marketing: boolean;
}

export interface CookieData {
  name: string;
  value: string;
  expires?: number;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
}

class CookieManager {
  private static instance: CookieManager;
  private preferences: CookiePreferences = {
    essential: true,
    analytics: false,
    performance: false,
    marketing: false
  };

  private constructor() {
    this.loadPreferences();
  }

  public static getInstance(): CookieManager {
    if (!CookieManager.instance) {
      CookieManager.instance = new CookieManager();
    }
    return CookieManager.instance;
  }

  // Set a cookie
  public setCookie(cookieData: CookieData): void {
    if (!this.canSetCookie(cookieData.name)) {
      return;
    }

    let cookieString = `${cookieData.name}=${cookieData.value}`;
    
    if (cookieData.expires) {
      const date = new Date();
      date.setTime(date.getTime() + (cookieData.expires * 24 * 60 * 60 * 1000));
      cookieString += `; expires=${date.toUTCString()}`;
    }
    
    if (cookieData.domain) {
      cookieString += `; domain=${cookieData.domain}`;
    }
    
    if (cookieData.path) {
      cookieString += `; path=${cookieData.path}`;
    }
    
    if (cookieData.secure) {
      cookieString += `; secure`;
    }
    
    if (cookieData.sameSite) {
      cookieString += `; samesite=${cookieData.sameSite}`;
    }

    document.cookie = cookieString;
  }

  // Get a cookie value
  public getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // Delete a cookie
  public deleteCookie(name: string, domain?: string, path?: string): void {
    let cookieString = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    
    if (domain) {
      cookieString += `; domain=${domain}`;
    }
    
    if (path) {
      cookieString += `; path=${path}`;
    }
    
    document.cookie = cookieString;
  }

  // Check if cookie can be set based on preferences
  private canSetCookie(cookieName: string): boolean {
    // Essential cookies are always allowed
    if (this.isEssentialCookie(cookieName)) {
      return true;
    }

    // Check other cookie types based on preferences
    if (this.isAnalyticsCookie(cookieName) && this.preferences.analytics) {
      return true;
    }

    if (this.isPerformanceCookie(cookieName) && this.preferences.performance) {
      return true;
    }

    if (this.isMarketingCookie(cookieName) && this.preferences.marketing) {
      return true;
    }

    return false;
  }

  // Cookie type detection
  private isEssentialCookie(name: string): boolean {
    const essentialCookies = [
      'cookie_consent',
      'user_preferences',
      'session_id',
      'csrf_token',
      'language',
      'theme'
    ];
    return essentialCookies.includes(name);
  }

  private isAnalyticsCookie(name: string): boolean {
    const analyticsCookies = [
      '_ga',
      '_gid',
      '_gat',
      '_gclid',
      'analytics_id',
      'user_behavior'
    ];
    return analyticsCookies.some(cookie => name.startsWith(cookie));
  }

  private isPerformanceCookie(name: string): boolean {
    const performanceCookies = [
      'performance_metrics',
      'load_time',
      'page_views',
      'bounce_rate',
      'conversion_tracking'
    ];
    return performanceCookies.some(cookie => name.startsWith(cookie));
  }

  private isMarketingCookie(name: string): boolean {
    const marketingCookies = [
      'marketing_id',
      'advertising_id',
      'remarketing',
      'social_media',
      'campaign_tracking'
    ];
    return marketingCookies.some(cookie => name.startsWith(cookie));
  }

  // Update cookie preferences
  public updatePreferences(newPreferences: Partial<CookiePreferences>): void {
    this.preferences = { ...this.preferences, ...newPreferences };
    this.savePreferences();
    this.applyPreferences();
  }

  // Get current preferences
  public getPreferences(): CookiePreferences {
    return { ...this.preferences };
  }

  // Save preferences to localStorage
  private savePreferences(): void {
    localStorage.setItem('cookie_preferences', JSON.stringify(this.preferences));
  }

  // Load preferences from localStorage
  private loadPreferences(): void {
    const saved = localStorage.getItem('cookie_preferences');
    if (saved) {
      try {
        this.preferences = { ...this.preferences, ...JSON.parse(saved) };
      } catch (error) {
        console.warn('Failed to load cookie preferences:', error);
      }
    }
  }

  // Apply preferences (delete cookies that are no longer allowed)
  private applyPreferences(): void {
    const allCookies = document.cookie.split(';').map(cookie => cookie.trim().split('=')[0]);
    
    allCookies.forEach(cookieName => {
      if (!this.canSetCookie(cookieName)) {
        this.deleteCookie(cookieName);
      }
    });
  }

  // Check if user has given consent
  public hasConsent(): boolean {
    return this.getCookie('cookie_consent') === 'true';
  }

  // Set consent
  public setConsent(consent: boolean): void {
    this.setCookie({
      name: 'cookie_consent',
      value: consent.toString(),
      expires: 365, // 1 year
      secure: true,
      sameSite: 'lax'
    });
  }

  // Analytics tracking
  public trackPageView(page: string): void {
    if (!this.preferences.analytics) return;

    this.setCookie({
      name: 'page_view',
      value: page,
      expires: 1
    });

    // Send to analytics service
    this.sendAnalytics('page_view', { page });
  }

  public trackEvent(event: string, data: any = {}): void {
    if (!this.preferences.analytics) return;

    this.sendAnalytics(event, data);
  }

  private sendAnalytics(event: string, data: any): void {
    // Google Analytics 4 integration
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', event, data);
    }

    // Custom analytics endpoint
    if (process.env.NODE_ENV === 'production') {
      fetch('/api/analytics', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event,
          data,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          url: window.location.href
        })
      }).catch(error => {
        console.warn('Analytics tracking failed:', error);
      });
    }
  }

  // Performance tracking
  public trackPerformance(metric: string, value: number): void {
    if (!this.preferences.performance) return;

    this.setCookie({
      name: `perf_${metric}`,
      value: value.toString(),
      expires: 7 // 1 week
    });
  }

  // SEO optimization
  public trackSEOInteraction(element: string, action: string): void {
    if (!this.preferences.analytics) return;

    this.trackEvent('seo_interaction', {
      element,
      action,
      page: window.location.pathname
    });
  }

  // User behavior tracking
  public trackUserBehavior(action: string, details: any = {}): void {
    if (!this.preferences.analytics) return;

    this.trackEvent('user_behavior', {
      action,
      details,
      timestamp: Date.now(),
      page: window.location.pathname
    });
  }

  // Clear all non-essential cookies
  public clearNonEssentialCookies(): void {
    const allCookies = document.cookie.split(';');
    
    allCookies.forEach(cookie => {
      const cookieName = cookie.trim().split('=')[0];
      if (!this.isEssentialCookie(cookieName)) {
        this.deleteCookie(cookieName);
      }
    });
  }
}

// Export singleton instance
export const cookieManager = CookieManager.getInstance();

// Utility functions
export const setCookie = (name: string, value: string, days: number = 30) => {
  cookieManager.setCookie({
    name,
    value,
    expires: days,
    secure: true,
    sameSite: 'lax'
  });
};

export const getCookie = (name: string): string | null => {
  return cookieManager.getCookie(name);
};

export const deleteCookie = (name: string) => {
  cookieManager.deleteCookie(name);
};

export const hasConsent = (): boolean => {
  return cookieManager.hasConsent();
};

export const setConsent = (consent: boolean) => {
  cookieManager.setConsent(consent);
};

export const updateCookiePreferences = (preferences: Partial<CookiePreferences>) => {
  cookieManager.updatePreferences(preferences);
};

export const getCookiePreferences = (): CookiePreferences => {
  return cookieManager.getPreferences();
};
